import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { articles, categories } from "@/mocks/articles";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-10 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const cat = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.published_at,
    image: article.image,
    publisher: {
      "@type": "Organization",
      name: "黑料官网",
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <article className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb items={[
                { label: "首页", href: "/" },
                { label: cat?.name || "分类", href: cat ? `/category/${cat.slug}` : undefined },
                { label: article.title },
              ]} />
            </div>

            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                {cat && (
                  <Link
                    to={`/category/${cat.slug}`}
                    className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors cursor-pointer"
                  >
                    {cat.name}
                  </Link>
                )}
                {article.is_hot && (
                  <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium flex items-center gap-1">
                    <span className="w-3 h-3 flex items-center justify-center"><i className="ri-fire-line"></i></span>
                    热门
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line"></i></span>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line"></i></span>
                  {article.published_at}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-eye-line"></i></span>
                  {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-chat-3-line"></i></span>
                  {article.comments} 评论
                </span>
              </div>
            </header>

            <div className="mb-8 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-[400px] md:h-[480px] object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none mb-10 text-foreground-800 leading-relaxed [&_p]:mb-5 [&_p]:text-foreground-800 [&_p]:leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mb-10">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="border-t border-background-200 pt-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <h3 className="text-lg font-semibold text-foreground-900 font-heading">相关爆料</h3>
                {cat && (
                  <Link
                    to={`/category/${cat.slug}`}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 cursor-pointer"
                  >
                    查看更多{cat.name}
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
                  </Link>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-100 rounded-lg overflow-hidden hover:bg-background-200 transition-all duration-200 cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={ra.thumbImage}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 leading-snug font-heading">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-2">{ra.published_at}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}